import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import JsonLd from "@/components/feature/JsonLd";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { site } from "@/mocks/site";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "网站定位",
    text: "我们不是新闻媒体，也不生产结论。深瞳档案更像一间开放的资料室，把散落在各处的公开信息按事件重新归档，方便你按线索去读。",
  },
  {
    icon: "ri-lightbulb-line",
    title: "发展理念",
    text: "比起抢第一时间，我们更在意一年之后这篇档案还有没有阅读价值。所以宁可慢一点，也要把时间线和前后关系补齐。",
  },
  {
    icon: "ri-book-open-line",
    title: "内容原则",
    text: "只基于公开信息、给出完整时间线、明确区分事实与判断、接受更正。四条原则贯穿每一篇档案的写作与修订。",
  },
];

const timeline = [
  { year: "起步", text: "从整理身边遇到的消费套路开始，最初只有一份共享文档。" },
  { year: "成形", text: "逐步划分出六个固定栏目，把不同类型的观察分开归档。" },
  { year: "现在", text: "持续更新公开信息整理与解读，接受读者提供可核对的线索。" },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 | 深瞳档案 幕后内幕内容档案站",
    description:
      "了解深瞳档案的品牌定位、发展理念与内容原则。我们以编辑视角整理公开信息，深扒幕后内幕与隐秘事件，帮助读者看清事件背后的深层逻辑与关联。",
    keywords: "深瞳档案,关于我们,内容原则,内幕整理,深度调查",
    path: "/about",
    type: "website",
  });

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于我们",
      url: "https://www.shentongdangan.cn/about",
      inLanguage: "zh-CN",
      description: site.intro,
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: site.name,
      url: "https://www.shentongdangan.cn/",
      email: site.email,
      slogan: site.slogan,
      description: site.intro,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://www.shentongdangan.cn/" },
        { "@type": "ListItem", position: 2, name: "关于我们", item: "https://www.shentongdangan.cn/about" },
      ],
    },
  ];

  return (
    <>
      <JsonLd data={schema} />
      <div className="border-b border-background-200 bg-background-100">
        <div className="container-page">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />
          <div className="pb-12">
            <span className="text-xs font-medium tracking-[0.2em] text-primary-600">ABOUT US</span>
            <h1 className="mt-3 max-w-3xl font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
              我们只做一件事：把散落的公开信息，重新整理成可读的档案
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-foreground-600">{site.intro}</p>
          </div>
        </div>
      </div>

      <div className="container-page py-14">
        <section aria-labelledby="position-heading" className="grid grid-cols-1 gap-5 md:grid-cols-3">
          <h2 id="position-heading" className="sr-only">
            品牌介绍
          </h2>
          {values.map((v) => (
            <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className={`${v.icon} text-xl`} aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.text}</p>
            </div>
          ))}
        </section>

        <section aria-labelledby="journey-heading" className="mt-16">
          <h2 id="journey-heading" className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            我们的历程
          </h2>
          <div className="mt-7 grid grid-cols-1 gap-4 md:grid-cols-3">
            {timeline.map((t, index) => (
              <div key={t.year} className="relative rounded-lg border border-background-200 bg-background-100 p-6">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-500 text-sm font-bold text-accent-950">
                  {index + 1}
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{t.year}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="voice-heading" className="mt-16 rounded-lg border border-background-200 bg-secondary-100 p-6 md:p-8">
          <h2 id="voice-heading" className="font-heading text-2xl font-bold text-foreground-950">
            读者眼中的深瞳档案
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              { text: "内容不吵，读起来像有人在你旁边把事情的来龙去脉慢慢讲清楚。", name: "读者 · 许", role: "设计师" },
              { text: "最喜欢的是它会明确说自己哪些是判断，这一点在现在很难得。", name: "读者 · 谭", role: "教师" },
              { text: "网络迷雾那一栏帮我改掉了看到截图就转发的习惯。", name: "读者 · 施", role: "运营" },
            ].map((v) => (
              <blockquote key={v.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <p className="text-sm leading-relaxed text-foreground-700">“{v.text}”</p>
                <footer className="mt-4 border-t border-background-200 pt-3 text-xs">
                  <p className="font-medium text-foreground-900">{v.name}</p>
                  <p className="mt-0.5 text-foreground-500">{v.role}</p>
                </footer>
              </blockquote>
            ))}
          </div>
        </section>

        <section aria-labelledby="contact-heading" className="mt-16 rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 id="contact-heading" className="font-heading text-2xl font-bold text-foreground-950">
                联系我们
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
                有线索、有更正、或者想聊聊某个选题，都可以直接写信给我们。我们会在 {site.workingHours} 之间回复。
              </p>
              <p className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-700">
                <span className="flex items-center gap-2">
                  <i className="ri-mail-line text-primary-700" aria-hidden="true" />
                  <a href={`mailto:${site.email}`} className="transition-colors hover:text-primary-700">
                    {site.email}
                  </a>
                </span>
                <span className="flex items-center gap-2">
                  <i className="ri-time-line text-primary-700" aria-hidden="true" />
                  {site.workingHours}
                </span>
              </p>
            </div>
            <Link
              to="/contact"
              className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              前往联系表单
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}